<template>
  <!--
 EntityCellElementLite, multi-root, без q-* / div-обёрток.
    Поведение 1:1 с EntityCellElement: 3 ветки (NonURL / File / Link) + role-теги.
 isFile использует auxStore.downloadFile, без диалогов / popover'ов.
  -->
  <template v-for="(link, i) in elements" :key="link.id || i">
    <span
      v-if="kind === 'nonurl'"
      class="qtl-entity-row qtl-entity-row--nonurl"
    >
      {{ link.name }}<span v-for="role in link.roles" :key="role.id || role" class="qtl-entity-role">#{{ role }}</span>
    </span>

    <span
      v-else-if="kind === 'file'"
      class="qtl-entity-link"
      role="link"
      tabindex="0"
      @click.prevent="onFileClick(link.eid, link.name)"
      @keydown.enter.prevent="onFileClick(link.eid, link.name)"
    >{{ link.name }}</span>

    <span v-else class="qtl-entity-row">
      <a
        v-if="link.id && link.name && link.name !== ' - '"
        class="qtl-entity-link"
        :href="link.url"
        target="_blank"
        rel="noopener"
      >{{ link.name }}</a>
      <span v-else>{{ link.name }}</span>
      <span v-for="role in link.roles" :key="role.id || role" class="qtl-entity-role">#{{ role }}</span>
    </span>
  </template>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { TableDataCellDisplayType } from "@/models/etc/types";
import type {
  CellDataEntity,
  CellDataItemAtStorage,
  CellDataItemQuantityAndMeasureUnit,
  TableDataCell,
} from "@/models/interfaces/entity-settings/querytable/querytable";
import { auxStore } from "@/store/auxillary";
import { buildClientUrl } from "@/utils/urlHelpers";

const props = defineProps<{ element: TableDataCell }>();

const aux = auxStore();

const kind = computed<"nonurl" | "file" | "link">(() => {
  const dt = props.element.DisplayType;
  if (
    dt === TableDataCellDisplayType.EntityAsWorker ||
    dt === TableDataCellDisplayType.EntityAsStorage ||
    dt === TableDataCellDisplayType.EntityAsAddress ||
    dt === TableDataCellDisplayType.ItemsAtStorages ||
    dt === TableDataCellDisplayType.ItemQuantityAndMeasureUnit
  ) {
    return "nonurl";
  }
  if (dt === TableDataCellDisplayType.EntityAsFileLink) return "file";
  return "link";
});

interface ElementLink {
  url: string;
  name: string;
  id: string | number;
  eid: number;
  roles: any[];
}

const elements = computed<ElementLink[]>(() => {
  const dt = props.element.DisplayType;
  const res: ElementLink[] = (props.element.Data || []).map((cell: any): ElementLink => {
    if (dt === TableDataCellDisplayType.ItemQuantityAndMeasureUnit) {
      const c = cell as CellDataItemQuantityAndMeasureUnit;
      return {
        url: "",
        name: `${c.MeasureUnit.Name}, ${c.Quantity}`,
        id: c.MeasureUnit.Id,
        eid: c.MeasureUnit.Id,
        roles: [],
      };
    }
    if (dt === TableDataCellDisplayType.ItemsAtStorages) {
      const c = cell as CellDataItemAtStorage;
      return {
        url: "",
        name: `${c.Storage.Name}, ${c.Quantity}`,
        id: c.Storage.Id,
        eid: c.Storage.Id,
        roles: [],
      };
    }
    let subUrl = "";
    let idPrefix = "";
    let nameStr = "";
    const c = cell as CellDataEntity;
    switch (dt) {
      case TableDataCellDisplayType.EntityAsClientLink:
        subUrl = "client/" + c.Id; idPrefix = "cli_"; nameStr = c.Name; break;
      case TableDataCellDisplayType.EntityAsProjectLink:
        subUrl = "project/" + c.Id; idPrefix = "pr_"; nameStr = c.Name; break;
      case TableDataCellDisplayType.EntityAsWorker:
        idPrefix = "w_"; nameStr = c.Name; break;
      case TableDataCellDisplayType.EntityAsStorage:
        idPrefix = "st_"; nameStr = c.Name; break;
      case TableDataCellDisplayType.EntityAsFileLink:
        subUrl = "" + c.Id; idPrefix = "w_"; nameStr = c.Name; break;
      case TableDataCellDisplayType.TagStatus:
      default:
        subUrl = "tag/" + c.Id; idPrefix = "tag_"; nameStr = c.Name; break;
    }
    return {
      url: buildClientUrl(subUrl),
      // пустое имя сущности отображаем пустой строкой (прочерк - только у параметров)
      name: nameStr || "",
      id: idPrefix + c.Id,
      eid: c.Id,
      roles: c.Roles || [],
    };
  });
  return res.length ? res : [{ url: "", name: "", id: 0, eid: 0, roles: [] }];
});

function onFileClick(arg: number, name: string) {
  aux?.downloadFile?.(arg, name);
}
</script>

<style scoped lang="scss">
.qtl-entity-row {
  display: block;
  max-width: 100%;
  font-size: 13px;
  overflow-wrap: anywhere;
  word-break: normal;
  margin-right: 0;
  line-height: 1.2;
}

.qtl-entity-row--nonurl {
  color: #333;
}

.qtl-entity-link {
  color: #438451;
  text-decoration: none;
  cursor: pointer;
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: normal;
}

.qtl-entity-link:hover {
  text-decoration: underline;
}

.qtl-entity-link:visited {
  color: #396742;
}

.qtl-entity-role {
  color: #888;
  margin-left: 4px;
}
</style>
